import { useEffect } from 'react';

export interface SEOConfig {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  structuredData?: Record<string, unknown>[];
}

export const usePageSEO = (config: SEOConfig) => {
  useEffect(() => {
    // Title
    document.title = config.title;

    // Meta tags
    const metaTags = [
      { name: 'description', content: config.description },
      { name: 'keywords', content: config.keywords || '' },
      { property: 'og:title', content: config.title },
      { property: 'og:description', content: config.description },
      { property: 'og:type', content: config.ogType || 'website' },
      { property: 'og:url', content: config.canonical || window.location.href },
      { property: 'og:image', content: config.ogImage || 'https://public.readdy.ai/ai/img_res/2462bd75b297831f78d190b9e53ff7ff.jpg' },
      { property: 'og:site_name', content: 'DMM FX完全ガイド' },
      { property: 'og:locale', content: 'ja_JP' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: config.title },
      { name: 'twitter:description', content: config.description },
      { name: 'twitter:image', content: config.ogImage || 'https://readdy.ai/api/search-image?query=DMM%20FX%20trading%20platform%20professional%20financial%20dashboard%20with%20charts%20and%20graphs%20modern%20clean%20interface%20blue%20and%20white%20color%20scheme%20high%20quality%20professional%20photography&width=1200&height=630&seq=og-default&orientation=landscape' },
    ];

    metaTags.forEach(({ name, property, content }) => {
      if (!content) return;
      
      const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
      let element = document.querySelector(selector);
      
      if (!element) {
        element = document.createElement('meta');
        if (name) element.setAttribute('name', name);
        if (property) element.setAttribute('property', property);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    });

    // Canonical URL
    if (config.canonical) {
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', config.canonical);
    }

    // Structured Data
    if (config.structuredData && config.structuredData.length > 0) {
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
      existingScripts.forEach(script => script.remove());

      config.structuredData.forEach((data) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.text = JSON.stringify(data);
        document.head.appendChild(script);
      });
    }

    // Cleanup function
    return () => {
      // Remove structured data scripts on unmount
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => script.remove());
    };
  }, [config]);
};
